/**
 * §P.12 · Las dos caras del directorio desde el panel.
 *
 * Lo que el club **enseña** —su ficha— y lo que el buscador le **devuelve** —las
 * reservas de clase de prueba—. La segunda es la que paga la primera.
 */

/** Copiado de `venues.geocode_precision`. Ver §X.1. */
export type EstadoDeReclamacion =
  'seeded' | 'claim_pending' | 'claimed' | 'hidden';

export interface FichaDelDirectorio {
  academy_id: string;
  slug: string;
  headline: string | null;
  description: string | null;
  master_bio: string | null;
  master_rank: string | null;
  years_active: number | null;
  accepts_trial: boolean;
  /**
   * Los tres de instalaciones y el de la profesora admiten nulo **a propósito**:
   * «no lo sé» no es «no lo tiene». Una ficha sembrada del censo federativo no sabe
   * si el local tiene aparcamiento, y decir que no sería mentir sobre un club que ni
   * siquiera se ha enterado de que está en el buscador.
   */
  has_parking: boolean | null;
  is_accessible: boolean | null;
  has_changing_rooms: boolean | null;
  female_instructor: boolean | null;
  languages: string[];
  price_from_cents: number | null;
  cover_asset_id: string | null;
  published_at: string | Date | null;
  claim_status: EstadoDeReclamacion;
  source: string | null;
  /**
   * Si **de verdad sale ahora mismo** en el buscador.
   *
   * No es lo mismo que tener `published_at`: una ficha publicada y luego retirada
   * conserva la fecha —es su historia— y deja de salir. Viene resuelto del servidor
   * justo para que nadie lo deduzca cruzando dos columnas y acabe pintando
   * «publicada» en verde sobre una ficha retirada.
   */
  is_listed: boolean;
  updated_at: string | Date;
}

export type EstadoDeReserva =
  'new' | 'contacted' | 'scheduled' | 'attended' | 'enrolled' | 'lost';

export interface ReservaDePrueba {
  id: string;
  group_id: string | null;
  group_name: string | null;
  contact_name: string;
  email: string | null;
  phone: string | null;
  student_name: string | null;
  student_age: number | null;
  /** ISO: 1 = lunes. */
  preferred_days: number[];
  preferred_time: string | null;
  message: string | null;
  source: 'directorio' | 'web_club' | 'app' | 'manual';
  status: EstadoDeReserva;
  scheduled_for: string | Date | null;
  converted_student_id: string | null;
  /**
   * Horas desde que llegó, mientras siga sin atender.
   *
   * **Es la única métrica que decide si esto funciona**: una reserva contestada en
   * dos horas se convierte; una contestada en tres días es una familia que ya está
   * apuntada en otro sitio. Viene a `null` en cuanto se atiende.
   */
  hours_waiting: number | null;
  created_at: string | Date;
  updated_at: string | Date;
}

/**
 * Cómo se llama cada estado **en la pantalla**, que no es como se llama en la base.
 *
 * `new` no es «nueva»: es «sin contestar», que es lo que hay que hacer con ella. Un
 * estado se nombra por la acción que pide, no por el momento en que se creó.
 */
export const ESTADOS_DE_RESERVA: Readonly<Record<EstadoDeReserva, string>> = {
  new: 'Sin contestar',
  contacted: 'Contactada',
  scheduled: 'Con día y hora',
  attended: 'Vino a probar',
  enrolled: 'Se apuntó',
  lost: 'No siguió',
};

/** Las abiertas: las que todavía piden algo de alguien. */
export const ABIERTAS: readonly EstadoDeReserva[] = [
  'new',
  'contacted',
  'scheduled',
  'attended',
];

/** De dónde vino. `directorio` es el buscador público de §X. */
export const ORIGENES: Readonly<Record<ReservaDePrueba['source'], string>> = {
  directorio: 'Del buscador',
  web_club: 'De tu web',
  app: 'De la App',
  manual: 'A mano',
};

/**
 * Cuánto lleva esperando, en palabras y con color.
 *
 * Los cortes no son redondos por gusto: **un día** es el plazo que el producto se
 * pone —la landing promete que el club contesta rápido— y **tres** es cuando ya no
 * hay nada que hacer. Pintarlo en rojo a las 72 h y no a las 24 sería decir que un
 * día de silencio es normal.
 */
export function espera(horas: number | null): {
  texto: string;
  clase: string;
} | null {
  if (horas === null) return null;

  const texto =
    horas < 1
      ? 'recién llegada'
      : horas < 24
        ? `${horas} h esperando`
        : `${Math.floor(horas / 24)} ${Math.floor(horas / 24) === 1 ? 'día' : 'días'} esperando`;

  const clase =
    horas >= 72
      ? 'pastilla-mal'
      : horas >= 24
        ? 'pastilla-espera'
        : 'pastilla-bien';

  return { texto, clase };
}
